import {
  zApiErrorResponse,
  zDirectoryUserList,
  ErrorCode,
  type AssetStatus,
  type AttachmentCategory,
} from '@mai-team-amusement/core';
import { ApiError, ApiMethod, apiRequest } from '@mai-team-amusement/ui';
import {
  FixedAssetApiPath,
  apiPath,
  zAssetCardView,
  zAssetHistory,
  zAssetList,
  zAssetManagerGrantList,
  zAssetPage,
  zAssetTypeList,
  zAssetTypeView,
  zAssetView,
  zAttachmentList,
  zAttachmentView,
  zAuditPage,
  zCapabilities,
  zEligibleManagerList,
  zImportPreview,
  zImportResult,
  zInventoryDetail,
  zInventoryItemView,
  zInventoryList,
  zInventoryView,
  zOpenInventoryItemList,
  zLocationList,
  zLocationView,
  zReassignResult,
  zSiteList,
  zSiteView,
  type AssetCreateInput,
  type AssetSiteInput,
  type AssetSort,
  type AssetTypeInput,
  type AssetTypeUpdate,
  type AssetUpdateInput,
  type DisposalInput,
  type ImportQuery,
  type InventoryCheckInput,
  type InventoryStartInput,
  type LocationInput,
  type LocationUpdate,
  type SiteSettingsInput,
} from '@mai-fixed-asset/contracts';
import { z } from 'zod';

const zNoContent = z.undefined();

type QueryValue = string | number | boolean | undefined;

/** `path?key=value…`, leaving out undefined values. */
export function withQuery(path: string, params: Readonly<Record<string, QueryValue>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text === '' ? path : `${path}?${text}`;
}

/** The asset list's filters as the web app holds them. */
export type AssetFilters = {
  groupId?: number;
  typeId?: number;
  statuses?: readonly AssetStatus[];
  q?: string;
  jpyMissing?: boolean;
  userDisabled?: boolean;
  codes?: readonly string[];
  sort?: AssetSort;
  desc?: boolean;
  page?: number;
  pageSize?: number;
};

function assetQuery(filters: AssetFilters): Record<string, QueryValue> {
  const { statuses, codes, q, jpyMissing, userDisabled, ...rest } = filters;
  return {
    ...rest,
    statuses: statuses === undefined ? undefined : statuses.join(','),
    codes: codes === undefined ? undefined : codes.join(','),
    q: q === undefined || q.trim() === '' ? undefined : q.trim(),
    jpyMissing: jpyMissing === true ? true : undefined,
    userDisabled: userDisabled === true ? true : undefined,
  };
}

const asset = (path: string, code: string) => apiPath(path, { code });

/** Uploads a file as the raw request body (the API's JSON helper sends JSON only). */
async function uploadFile<T>(path: string, body: Blob, schema: z.ZodType<T>): Promise<T> {
  const response = await fetch(path, {
    method: ApiMethod.Post,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/octet-stream' },
    body,
  });
  const payload: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const parsed = zApiErrorResponse.safeParse(payload);
    throw new ApiError(
      response.status,
      parsed.success ? parsed.data.error : { code: ErrorCode.Internal },
    );
  }
  return schema.parse(payload);
}
const withBody = (method: ApiMethod, body: unknown) => ({ method, body });

export const assetApi = {
  capabilities: () => apiRequest(FixedAssetApiPath.Capabilities, zCapabilities),
  // Admin
  assetManagerGrants: () =>
    apiRequest(FixedAssetApiPath.AdminAssetManagers, zAssetManagerGrantList),
  grantAssetManager: (sub: string) =>
    apiRequest(apiPath(FixedAssetApiPath.AdminAssetManager, { sub }), zNoContent, {
      method: ApiMethod.Put,
    }),
  revokeAssetManager: (sub: string) =>
    apiRequest(apiPath(FixedAssetApiPath.AdminAssetManager, { sub }), zNoContent, {
      method: ApiMethod.Delete,
    }),
  auditLogs: (page: number, pageSize: number) =>
    apiRequest(withQuery(FixedAssetApiPath.AuditLogs, { page, pageSize }), zAuditPage),
  // Master data
  sites: () => apiRequest(FixedAssetApiPath.Sites, zSiteList),
  saveSite: (groupId: number, input: SiteSettingsInput) =>
    apiRequest(
      apiPath(FixedAssetApiPath.Site, { groupId }),
      zSiteView,
      withBody(ApiMethod.Put, input),
    ),
  assetTypes: () => apiRequest(FixedAssetApiPath.AssetTypes, zAssetTypeList),
  createAssetType: (input: AssetTypeInput) =>
    apiRequest(FixedAssetApiPath.AssetTypes, zAssetTypeView, withBody(ApiMethod.Post, input)),
  updateAssetType: (id: number, input: AssetTypeUpdate) =>
    apiRequest(
      apiPath(FixedAssetApiPath.AssetType, { id }),
      zAssetTypeView,
      withBody(ApiMethod.Patch, input),
    ),
  locations: (groupId?: number) =>
    apiRequest(withQuery(FixedAssetApiPath.Locations, { groupId }), zLocationList),
  createLocation: (input: LocationInput) =>
    apiRequest(FixedAssetApiPath.Locations, zLocationView, withBody(ApiMethod.Post, input)),
  updateLocation: (id: number, input: LocationUpdate) =>
    apiRequest(
      apiPath(FixedAssetApiPath.Location, { id }),
      zLocationView,
      withBody(ApiMethod.Patch, input),
    ),
  // People
  searchUsers: (query: string) =>
    apiRequest(withQuery(FixedAssetApiPath.Users, { query }), zDirectoryUserList),
  eligibleManagers: () => apiRequest(FixedAssetApiPath.EligibleManagers, zEligibleManagerList),
  reassign: (fromSub: string, toSub: string) =>
    apiRequest(
      FixedAssetApiPath.ReassignManager,
      zReassignResult,
      withBody(ApiMethod.Post, { fromSub, toSub }),
    ),
  // Assets
  assets: (filters: AssetFilters) =>
    apiRequest(withQuery(FixedAssetApiPath.Assets, assetQuery(filters)), zAssetPage),
  exportUrl: (filters: AssetFilters) =>
    withQuery(FixedAssetApiPath.AssetExport, assetQuery(filters)),
  myAssets: () => apiRequest(FixedAssetApiPath.MyAssets, zAssetList),
  asset: (code: string) => apiRequest(asset(FixedAssetApiPath.Asset, code), zAssetView),
  card: (code: string) => apiRequest(asset(FixedAssetApiPath.AssetCard, code), zAssetCardView),
  history: (code: string) => apiRequest(asset(FixedAssetApiPath.AssetHistory, code), zAssetHistory),
  createAsset: (input: AssetCreateInput) =>
    apiRequest(FixedAssetApiPath.Assets, zAssetView, withBody(ApiMethod.Post, input)),
  updateAsset: (code: string, input: AssetUpdateInput) =>
    apiRequest(asset(FixedAssetApiPath.Asset, code), zAssetView, withBody(ApiMethod.Patch, input)),
  setUser: (code: string, version: number, userSub: string | null) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetUser, code),
      zAssetView,
      withBody(ApiMethod.Put, { version, userSub }),
    ),
  setManager: (code: string, version: number, managerSub: string) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetManager, code),
      zAssetView,
      withBody(ApiMethod.Put, { version, managerSub }),
    ),
  setLocation: (code: string, version: number, locationId: number | null) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetLocation, code),
      zAssetView,
      withBody(ApiMethod.Put, { version, locationId }),
    ),
  setSite: (code: string, input: AssetSiteInput) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetSite, code),
      zAssetView,
      withBody(ApiMethod.Put, input),
    ),
  setStatus: (code: string, version: number, status: AssetStatus) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetStatus, code),
      zAssetView,
      withBody(ApiMethod.Put, { version, status }),
    ),
  dispose: (code: string, input: DisposalInput) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetDisposal, code),
      zAssetView,
      withBody(ApiMethod.Post, input),
    ),
  attachments: (code: string) =>
    apiRequest(asset(FixedAssetApiPath.AssetAttachments, code), zAttachmentList),
  uploadAttachment: (code: string, category: AttachmentCategory, filename: string, body: Blob) =>
    uploadFile(
      withQuery(asset(FixedAssetApiPath.AssetAttachments, code), { category, filename }),
      body,
      zAttachmentView,
    ),
  attachmentUrl: (code: string, id: number) =>
    apiPath(FixedAssetApiPath.AssetAttachmentContent, { code, id }),
  removeAttachment: (code: string, id: number) =>
    apiRequest(apiPath(FixedAssetApiPath.AssetAttachment, { code, id }), zNoContent, {
      method: ApiMethod.Delete,
    }),
  revertDisposal: (code: string, version: number) =>
    apiRequest(
      asset(FixedAssetApiPath.AssetDisposalRevert, code),
      zAssetView,
      withBody(ApiMethod.Post, { version }),
    ),
  // Inventory counts
  inventories: () => apiRequest(FixedAssetApiPath.Inventories, zInventoryList),
  inventory: (id: number) =>
    apiRequest(apiPath(FixedAssetApiPath.Inventory, { id }), zInventoryDetail),
  startInventory: (input: InventoryStartInput) =>
    apiRequest(FixedAssetApiPath.Inventories, zInventoryView, withBody(ApiMethod.Post, input)),
  checkItem: (id: number, code: string, input: InventoryCheckInput) =>
    apiRequest(
      apiPath(FixedAssetApiPath.InventoryItem, { id, code }),
      zInventoryItemView,
      withBody(ApiMethod.Put, input),
    ),
  closeInventory: (id: number) =>
    apiRequest(apiPath(FixedAssetApiPath.InventoryClose, { id }), zInventoryView, {
      method: ApiMethod.Post,
    }),
  inventoryExportUrl: (id: number) => apiPath(FixedAssetApiPath.InventoryExport, { id }),
  myInventoryItems: () => apiRequest(FixedAssetApiPath.MyInventoryItems, zOpenInventoryItemList),
  assetInventoryItems: (code: string) =>
    apiRequest(asset(FixedAssetApiPath.AssetInventoryItems, code), zOpenInventoryItemList),
  // CSV import
  importTemplateUrl: FixedAssetApiPath.ImportTemplate,
  previewImport: (file: Blob, query: ImportQuery) =>
    uploadFile(withQuery(FixedAssetApiPath.ImportPreview, query), file, zImportPreview),
  commitImport: (file: Blob, query: ImportQuery) =>
    uploadFile(withQuery(FixedAssetApiPath.Imports, query), file, zImportResult),
};

/** TanStack Query keys (prefixes invalidate everything below them). */
export const AssetQueryKey = {
  Capabilities: ['asset', 'capabilities'],
  AssetManagerGrants: ['asset', 'asset-manager-grants'],
  Audit: ['asset', 'audit'],
  Sites: ['asset', 'sites'],
  AssetTypes: ['asset', 'asset-types'],
  Locations: ['asset', 'locations'],
  EligibleManagers: ['asset', 'eligible-managers'],
  UserSearch: ['asset', 'user-search'],
  Assets: ['asset', 'assets'],
  MyAssets: ['asset', 'my-assets'],
  Asset: ['asset', 'asset'],
  Card: ['asset', 'card'],
  History: ['asset', 'history'],
  Attachments: ['asset', 'attachments'],
  Labels: ['asset', 'labels'],
  Inventories: ['asset', 'inventories'],
  Inventory: ['asset', 'inventory'],
  MyInventoryItems: ['asset', 'my-inventory-items'],
  AssetInventoryItems: ['asset', 'asset-inventory-items'],
} as const;
